import axios from "axios";
import {
  sanitizeEdiLayoutCreatePayload,
  sanitizeEdiLayoutUpdatePayload,
} from '../utils/EdiHelper';

const backendApi = process.env.REACT_APP_FINANCEIRO_BACK_URL;

// CARGOZILLA-8231 — filtro de contas do dashboard. `contas` só entra no corpo quando há
// seleção: sem filtro o payload continua idêntico ao que o backend sempre recebeu.
const corpoDashboard = (tenant, contas) => (
  contas && contas.length ? { tenant, contas } : { tenant }
);

export default class Api {
  constructor(token, onError) {
    this.axios = axios.create({
      baseURL: backendApi,
      headers: {
        Authorization: token,
      },
    });

    this.axios.interceptors.response.use((response) => response, onError);
  }

  login(username, password) {
    return this.axios.post("/user/login", { username, password });
  }

  validateToken(token) {
    return this.axios.post("/validate-token", { token });
  }

  getUserViewPermission(view) {
    return this.axios.get(`/user/view-permissions/${view}`);
  }

  sendConfirmationCodeEmail(username) {
    return this.axios.post("/user/send-confirmation-code-email", { username });
  }

  validateConfirmationCode(username, code) {
    return this.axios.post("/user/validate-confirmation-code", {
      username,
      code,
    });
  }

  recoveryUsernamePassword(username, newPassword) {
    return this.axios.put("/user/recovery-username-password", {
      username,
      newPassword,
    });
  }

  //Receitas
  async getIncomes(amount = 10, offset = 0, filters) {
    try {
      const response = await this.axios.get(
        `/receita/abertas-grid/${filters.tenant}?offset=${offset}&amount=${amount}`,
        {
          params: {
            ...filters,
          },
        }
      );
      return { data: response.data.data };
    } catch (error) {
      return { error };
    }
  }

  async getMissingPaymentIncomes(amount = 10, offset = 0, filters) {
    try {
      const response = await this.axios.get(
        `/receita/inadimplentes-grid/${filters.tenant}?offset=${offset}&amount=${amount}`,
        {
          params: {
            ...Api.removeBlankFields(filters),
            getAllIncomes: filters.getAllIncomes,
            period: filters.period,
          },
        }
      );
      return { data: response.data.data };
    } catch (error) {
      return { error };
    }
  }

  async getPaidIncomes(amount = 10, offset = 0, filters) {
    try {
      const response = await this.axios.get(
        `/receita/liquidadas-grid/${filters.tenant}?offset=${offset}&amount=${amount}`,
        {
          params: {
            ...filters,
          },
        }
      );
      return { data: response.data.data };
    } catch (error) {
      return { error };
    }
  }

  async atualizarBoletoReceita(id) {
    return this.axios.get(`receita/boleto/integracao-api/${id}`);
  }

  async atualizarBoletosLote(incomes) {
    return this.axios.post(`receita/boleto/integracao-api/`, {
      incomes,
    });
  }

  async gerarBoletoAvulso(incomes) {
    return this.axios.post(`receita/boleto/integracao-api-avulso`, incomes);
  }

  getSingleIncome(id) {
    return this.axios.get(`/receita/completa/${id}`);
  }

  getReceitaCompleta(id) {
    return this.axios.get(`/receita/completa/${id}`);
  }

  getReceitaConhecimentos(id) {
    return this.axios.get(`/receita/conhecimentos/${id}`);
  }

  cancelarReceita(id) {
    return this.axios.put(`/receita/cancelar/${id}`);
  }

  searchIncomes(payload) {
    return this.axios.post(`/receita/grid/search`, {
      dataInicio: payload.dataVencimentoInicio,
      dataFim: payload.dataVencimentoFim,
      sacadoId: payload.sacadoId,
      loteFaturamento: payload.loteFaturamento,
      fatura: payload.numeroFatura,
      tenant: this.state.tenant,
    });
  }

  postReceitaFaturamento(faturas) {
    return this.axios.post(`/receita/faturamento`, faturas);
  }

  // Cobrança de fatura ao cliente pela tela de Receitas (mesmo envelope `{ success, data }` do
  // ciclo de faturamento). `tenant` em params nos GET e no body no POST.

  // Preenchimento do modal: receitas agrupadas por cliente (faturas elegíveis, excluídas com
  // motivo, destinatários do cadastro) + mensagem padrão do tenant.
  getPreenchimentoCobrancaReceita({ receitaIds, tenant }) {
    return this.axios.get(`/receita/cobranca/preenchimento`, {
      params: { receitaIds: (receitaIds || []).join(","), tenant },
    });
  }

  // Envio assíncrono (202). `mensagem`/`replyTo` são opcionais e só entram no body quando preenchidos.
  enviarCobrancaReceita({
    tenant, mensagem, replyTo, clientes,
  }) {
    return this.axios.post(`/receita/cobranca/enviar`, {
      tenant,
      ...Api.removeBlankFields({ mensagem, replyTo }),
      clientes,
    });
  }

  // Listagem paginada dos envios de cobrança do tenant (todas as origens). Filtros vazios são omitidos.
  getCobrancasEnviosReceita({
    tenant, situacao, pessoaId, dataInicio, dataFim, page, limit,
  }) {
    return this.axios.get(`/receita/cobranca/envios`, {
      params: Api.removeBlankFields({
        tenant, situacao, pessoaId, dataInicio, dataFim, page, limit,
      }),
    });
  }

  receitaLiquidarParcial(id, payload) {
    return this.axios.put(`/receita/liquidar-parcial/${id}`, {
      valor: payload.valor,
      dtMovimentacao: payload.dtMovimentacao,
      historico: payload.historico,
      contaId: payload.contaId,
      tenant: payload.tenant,
    });
  }

  receitaLiquidarPorConhecimento(
    id,
    date,
    valor,
    historico,
    contaId,
    conhecimentos
  ) {
    return this.axios.put(`/receita/liquidar-conhecimento/${id}`, {
      dtMovimentacao: date,
      valor: valor,
      historico: historico,
      contaId: contaId,
      conhecimentos: conhecimentos,
    });
  }

  receitaLiquidarTotal(id, valor, date, historico, contaId) {
    return this.axios.put(`/receita/liquidar-total/${id}`, {
      dtMovimentacao: date,
      historico: historico,
      contaId: contaId,
      valor,
    });
  }

  createReceita(receita) {
    return this.axios.post(`/receita/create`, receita);
  }

  updateReceita(id, receita) {
    return this.axios.put(`/receita/update/${id}`, receita);
  }

  searchReceita(payload) {
    return this.axios.post(`/receita/grid/search`, payload);
  }

  getReceitaArquivo(tenant, id) {
    return this.axios.get(`/receita/arquivo/${tenant}/${id}`);
  }

  getMultiplasReceitasFatura(tenant, arrayIds) {
    return this.axios.post("/receita/multiplos-arquivos", { arrayIds, tenant });
  }

  getFaturaArquivo(tenant, id) {
    return this.axios.get(`/receita/arquivo/fatura/${tenant}/${id}`);
  }

  getRelatoriosReceita(receita) {
    return this.axios.post(`/receita/relatorios/receita`, receita);
  }

  getRelatoriosReceitaPlanos(receita) {
    return this.axios.post(`/receita/relatorios/receita-planos`, receita);
  }

  getReceitaDescontoRelatorio(receita) {
    return this.axios.post(`/receita/relatorios/desconto`, receita);
  }

  estornarReceita(id) {
    return this.axios.put(`/receita/estornar/${id}`);
  }

  gerarRemessaAlteracao(boleto, tenant) {
    return this.axios.post(`/receita/gerar-remessa-alteracao`, {
      boletoId: boleto.id,
      nossoNumero: boleto.nossoNumero,
      numeroDocumento: boleto.numeroDocumento,
      idIntegracao: boleto.idIntegracao,
      cnpjCedente: boleto.cnpjCedente,
      tenant: tenant,
      tituloDataVencimento: boleto.tituloDataVencimento,
      tituloValor: boleto.tituloValor,
    });
  }

  async getIndicatorIncomes(filters = {}) {
    try {
      const response = await this.axios.get(
        `/receita/indicadores/${filters.tenant}`,
        {
          params: {
            ...Api.removeBlankFields(filters),
          },
        }
      );
      return response.data;
    } catch (error) {
      return { error };
    }
  }

  gerarRemessaReceitas(payload) {
    return this.axios.post("/receita/boleto/gerar-remessa-receitas", payload);
  }

  gerarNfsReceitas(payload) {
    return this.axios.post("/receita/nota-servico/gerar-nfs-receitas", payload);
  }

  getReceitaHistorico(id) {
    return this.axios.get(`/receita/historico/${id}`);
  }

  //Boleto
  getBoleto(receitaId) {
    return this.axios.get(`/boleto/arquivo/${receitaId}`);
  }

  async getBoletos(amount = 10, offset = 0, filters) {
    try {
      const response = await this.axios.get(
        `/boleto/por-tenant/${filters.tenant}?offset=${offset}&amount=${amount}`,
        {
          params: {
            ...filters,
          },
        }
      );
      return { data: response.data.data };
    } catch (error) {
      return { error };
    }
  }

  getLotesBoleto(filters) {
    return this.axios.post("/boleto/get-lotes-boleto/", filters);
  }

  getBoletosLote(payload) {
    return this.axios.post("/boleto/get-boletos-lote/", payload);
  }

  downloadArquivoRemessa(boletoId) {
    return this.axios.get(`/boleto/remessa/${boletoId}`, {
      responseType: "blob",
    });
  }

  incluirBoleto(boleto) {
    return this.axios.post(`/boleto/incluir-boleto`, boleto);
  }

  uploadRetorno(payload) {
    return this.axios.post("/boleto/upload-retorno", payload);
  }

  gerarRemessa(ids, idsIntegracao, cnpjCedente, padraoCnab, tenant) {
    return this.axios.post(`/boleto/gerar-remessa`, {
      ids,
      idsIntegracao,
      cnpjCedente: cnpjCedente,
      padraoCnab,
      tenant: tenant,
    });
  }

  cancelarBoleto(boleto, tenant) {
    return this.axios.post(`/boleto/cancelar-boleto`, {
      id: boleto.id,
      idIntegracao: boleto.idIntegracao,
      cnpjCedente: boleto.cnpj_cedente,
      numeroDocumento: boleto.titulo_numero_documento,
      tenant: tenant,
    });
  }

  getBoletoHistorico(receitaId) {
    return this.axios.get(`/boleto/historico/${receitaId}`);
  }

  listarBancos() {
    return this.axios.get(`/boleto/listar-bancos`);
  }

  getConvenio(tenant, convenioId) {
    return this.axios.get(`/convenio/completo/${tenant}/${convenioId}`);
  }

  getArquivosAlteracao(boleto) {
    return this.axios.get(`/boleto/alteracao/all/${boleto}`);
  }

  baixarArquivoAlteracao(arquivo) {
    return this.axios.get(`/boleto/alteracao/download/${arquivo}`);
  }

  getBoletosByReceitasIds(filters) {
    return this.axios.get("/boleto/get-by-receitas-ids", { params: filters });
  }

  // Centro de Custo

  getCentrosDeCustos(tenant) {
    return this.axios.get(`/centro-de-custo/tenant`, { params: { tenant } });
  }

  getSingleCentroDeCusto(id) {
    return this.axios.get(`/centro-de-custo/show/${id}`);
  }

  createCentroDeCusto(centroDeCusto) {
    return this.axios.post(`/centro-de-custo/create`, centroDeCusto);
  }

  updateCentroDeCusto(id, centroDeCusto) {
    return this.axios.put(`/centro-de-custo/update/${id}`, centroDeCusto);
  }

  centrosDeCustoGridSearch(payload) {
    return this.axios.post("/centro-de-custo/grid/search", payload);
  }

  cancelCentroDeCusto(centroDeCustoId) {
    return this.axios.delete(`/centro-de-custo/cancelar/${centroDeCustoId}`);
  }

  gerarEstruturaPadraoCentroCusto(tenant) {
    return this.axios.post("/centro-de-custo/estrutura-padrao", { tenant });
  }

  // Contas

  getMovimentacaoBancaria(tenant) {
    return this.axios.get(`/conta/movimentacao-bancaria/${tenant}`);
  }

  getMovBancariaByMonth(payload) {
    return this.axios.post(`/conta/movimentacao-bancaria-mes/`, payload);
  }

  getMovimentacaoConta(tenant, contaId) {
    return this.axios.post(`/conta/movimentacao-conta`, {
      tenant,
      contaId: contaId,
    });
  }

  createConta(conta) {
    return this.axios.post(`/conta/create`, conta);
  }

  updateConta(id, conta) {
    return this.axios.put(`/conta/update/${id}`, conta);
  }

  getContas(tenant) {
    return this.axios.post(`/conta/contas-por-tenant`, { tenant });
  }

  getSingleConta(id) {
    return this.axios.get(`/conta/show/${id}`);
  }

  // Histórico de alterações de cadastro da conta (CARGOZILLA-8399)
  getContaAlteracoes(contaId) {
    return this.axios.get(`/conta/alteracoes/${contaId}`);
  }

  transferChargeBack(movimentacao) {
    return this.axios.post("/conta/transferencia/estornar", movimentacao);
  }

  paymentReport(payload) {
    return this.axios.post("/conta/pagamentos", payload);
  }

  getTransfers(payload) {
    return this.axios.post("/conta/get-transfers", payload);
  }

  // Pessoas
  getParceirosByTenant(payload) {
    return this.axios.post("/pessoa/parceiros-por-tenant", payload);
  }

  async getPessoas(amount = 10, offset = 0, filters) {
    try {
      const response = await this.axios.get(
        `/pessoa/${filters.tenant}?offset=${offset}&amount=${amount}`,
        {
          params: {
            ...filters,
          },
        }
      );
      return { data: response.data.data };
    } catch (error) {
      return { error };
    }
  }

  async atualizaRemoverFluxoEmail(payload) {
    try {
      const response = await this.axios.put('/pessoa/atualiza-remover-fluxo-email', payload);
      return response;
    } catch (error) {
      return { error };
    }
  }  

  /**
   * @description This method will be deprecated, use getPessoas instead of this
   * @param {String} tenant
   * @param {Bollean} desconsiderarInativos
   */
  getAllPessoas(tenant, desconsiderarInativos) {
    return this.axios.post(`/pessoa/pessoas-por-tenant`, {
      tenant,
      desconsiderarInativos,
    });
  }

  getFornecedores(tenant, inativar) {
    return this.axios.get(
      `/pessoa/fornecedores-por-tenant/${tenant}?inativar=${inativar}`
    );
  }

  getSinglePessoa(id) {
    return this.axios.get(`/pessoa/show/${id}`);
  }

  getPessoasGrid(tenant) {
    return this.axios.get(`/pessoa/pessoas-grid/${tenant}`, { tenant });
  }

  getPessoaCompleta(tenant, id) {
    return this.axios.get(`/pessoa/completa/${tenant}/${id}`);
  }

  getPessoasFiliais(tenant) {
    return this.axios.get(`/pessoa/filiais/${tenant}`);
  }

  getCedentePorTenant(tenant) {
    return this.axios.get(`/pessoa/cedentes-por-tenant/${tenant}`);
  }

  createPessoa(pessoa) {
    return this.axios.post(`/pessoa/create`, pessoa);
  }

  updatePessoa(id, pessoa) {
    return this.axios.put(`/pessoa/update/${id}`, pessoa);
  }

  getPessoaMovimentacoes(id, tenant) {
    return this.axios.get(`/pessoa/movimentacoes/${id}/${tenant}`);
  }

  checkPessoaInativar(id) {
    return this.axios.get(`pessoa/check-inativar/${id}`);
  }

  getPessoaContatos(pessoaIdParam, contatoIdParam, customTenant) {
    const contatoId = contatoIdParam || "";
    // Esta versão do axios NÃO omite header com valor undefined: serializa o literal
    // "undefined", que o backend passou a rejeitar com 400 (sanitizeTenant). Só enviamos o
    // header quando há tenant de fato — sem ele, o escopo vem do token, como nas demais
    // chamadas.
    const config = customTenant ? { headers: { tenant: customTenant } } : {};
    return this.axios.get(`/pessoa/${pessoaIdParam}/contato/${contatoId}`, config);
  }

  async editPessoaContato(pessoaIdParam, contatoId, values) {
    try {
      const response = await this.axios.put(
        `/pessoa/${pessoaIdParam}/contato/${contatoId}`,
        {
          ...values,
        }
      );
      return response;
    } catch (error) {
      return { error };
    }
  }

  async removePessoaContato(idPessoa, idContact) {
    try {
      const response = await this.axios.delete(
        `/pessoa/${idPessoa}/contato/${idContact}`
      );
      return response;
    } catch (error) {
      return { error };
    }
  }

  async createPessoaContato(idPessoa, values) {
    try {
      const response = await this.axios.post(`pessoa/${idPessoa}/contato`, {
        ...values,
      });
      return response;
    } catch (error) {
      return { error };
    }
  }

  getAddressByCep(cep) {
    return axios.get(`https://viacep.com.br/ws/${cep}/json`);
  }

  getDataByCNPJ(cnpj) {
    return this.axios.get(`/receita-ws/cnpj/${cnpj}`);
  }

  getMatrizByTenant(tenant) {
    return this.axios.get(`/pessoa/matriz-por-tenant/${tenant}`);
  }

  transferMoney(transaction) {
    return this.axios.post("/conta/transferencia/realizar", transaction);
  }

  getRelatoriosPessoa(pessoa) {
    return this.axios.post(`/pessoa/relatorios/pessoa`, pessoa);
  }

  uploadCertificadoDigital(certificado) {
    return this.axios.post(`/pessoa/upload-certificado`, certificado);
  }

  // Conta Orçamentária

  async getContasOrcamentarias(amount = 10, offset = 0, filters) {
    try {
      const response = await this.axios.get(
        `/conta-orcamentaria/${filters.tenant}?offset=${offset}&amount=${amount}`,
        {
          params: {
            ...filters,
          },
        }
      );
      return { data: response.data.data };
    } catch (error) {
      return { error };
    }
  }

  getSingleContaOrcamentaria(id) {
    return this.axios.get(`/conta-orcamentaria/show/${id}`);
  }

  createContaOrcamentaria(contaOrcamentaria) {
    return this.axios.post(`/conta-orcamentaria/create`, contaOrcamentaria);
  }

  updateContaOrcamentaria(id, contaOrcamentaria) {
    return this.axios.put(
      `/conta-orcamentaria/update/${id}`,
      contaOrcamentaria
    );
  }

  cancelContaOrcamentaria(contaOrcamentariaId) {
    return this.axios.delete(
      `/conta-orcamentaria/cancelar/${contaOrcamentariaId}`
    );
  }

  getAllContasOrcamentariaTenant(payload) {
    return this.axios.post("/conta-orcamentaria/all", payload);
  }

  getAllContasOrcamentariasOnlyLastLevel(payload) {
    return this.axios.post("/conta-orcamentaria/only-last-level", payload);
  }

  gerarEstruturaPadraoContaOrcamentaria(tenant) {
    return this.axios.post("/conta-orcamentaria/estrutura-padrao", { tenant });
  }

  importarXlsxContaOrcamentaria(payload) {
    return this.axios.post("/conta-orcamentaria/estrutura-arquivo", payload);
  }

  // Convenios

  getConvenios(tenant) {
    return this.axios.get(`/convenio/convenios-por-tenant/${tenant}`);
  }

  getSingleConvenio(id, tenant) {
    return this.axios.get(`/convenio/show/${id}`, { params: {tenant} });
  }

  updateConvenio(id, convenio) {
    return this.axios.put(`/convenio/update/${id}`, convenio);
  }

  getConveniosFatura(filters) {
    return this.axios.get(`/convenio/convenios-fatura`, { params: filters });
  }

  getNextNsuConvenio() {
    return this.axios.get(`/nsu-convenio/next`);
  }

  // Histórico campo a campo do convênio (CARGOZILLA-8398)
  getConvenioHistorico(convenioId) {
    return this.axios.get(`/convenio/historico/${convenioId}`);
  }

  getContasIntegradas(tenant) {
    return this.axios.get(`/conta/contas-integradas/${tenant}`);
  }

  getTotalRevenuesMonth(tenant, contas) {
    return this.axios.post(`/receita/total-mes`, corpoDashboard(tenant, contas));
  }

  getTotalExpensesMonth(tenant, contas) {
    return this.axios.post(`/despesa/total-mes`, corpoDashboard(tenant, contas));
  }

  // CARGOZILLA-8230 — card "Despesas Recorrentes" do dashboard. Só o tenant é obrigatório;
  // sem período o backend assume o mês corrente, igual ao card "Despesas".
  getTotalDespesasRecorrentesMonth(tenant, contas) {
    return this.axios.post(`/despesa/recorrentes/total-mes`, corpoDashboard(tenant, contas));
  }

  getBalanceAmount(tenant, contas) {
    return this.axios.post(`/conta/saldo-por-tenant`, corpoDashboard(tenant, contas));
  }

  getTotalInadimplenciaMonth(tenant, contas) {
    return this.axios.post(`/receita/inadimplencia/total-mes`, corpoDashboard(tenant, contas));
  }

  getTotalIncomeDay(tenant, contas) {
    return this.axios.post(`/receita/indicadores/total-dia`, corpoDashboard(tenant, contas));
  }

  getTotalExpensesDay(tenant, contas) {
    return this.axios.post(`/despesa/indicadores/total-dia`, corpoDashboard(tenant, contas));
  }

  getDashboardChartsInfo(tenant, contas) {
    return this.axios.post(`/dashboard/fluxo-caixa-mes`, corpoDashboard(tenant, contas));
  }

  //Despesas
  getDespesasAbertas(amount = 10, offset = 0, filters) {
    return this.axios.get(
      `/despesa/despesas-abertas-grid/${filters.tenant}?offset=${offset}&amount=${amount}`,
      {
        params: {
          ...filters,
        },
      }
    );
  }

  getDespesasInadimplentes(amount = 10, offset = 0, filters) {
    return this.axios.get(
      `/despesa/inadimplentes-grid/${filters.tenant}?offset=${offset}&amount=${amount}`,
      {
        params: {
          ...filters,
        },
      }
    );
  }

  getDespesasLiquidadas(amount = 10, offset = 0, filters) {
    return this.axios.get(
      `/despesa/liquidadas-grid/${filters.tenant}?offset=${offset}&amount=${amount}`,
      {
        params: {
          ...filters,
        },
      }
    );
  }

  cancelarDespesa(id) {
    return this.axios.put(`/despesa/cancelar/${id}`);
  }

  cancelarReceitaRecorrente(id, date) {
    return this.axios.post(`/despesa-recorrente/cancelar/${id}`, date);
  }

  detalharDespesa(id, tenant) {
    return this.axios.get(`/despesa/detalhar`, {
      params: {
        id: id,
        tenant: tenant,
      },
    });
  }

  detalharDespesaRecorrente(tenant, recorrenteId, vencimento) {
    return this.axios.post(`/despesa-recorrente/detalhar`, {
      tenant,
      recorrenteId,
      vencimento,
    });
  }

  clonarDespesa(payload) {
    return this.axios.post(`/despesa/clonar`, payload);
  }

  searchDespesa(amount = 10, offset = 0, filters) {
    return this.axios.get(
      `/despesa/grid/search/${filters.tenant}?offset=${offset}&amount=${amount}`,
      {
        params: {
          ...filters,
        },
      }
    );
  }

  despesasConcilicacaoGridSearch(payload) {
    return this.axios.post("/despesa/conciliacao-grid/search", payload);
  }

  despesaLiquidarParcial(id, payload) {
    return this.axios.put(`/despesa/liquidar-parcial/${id}`, {
      valor: payload.valor,
      dtMovimentacao: payload.dtMovimentacao,
      historico: payload.historico,
      contaId: payload.contaId,
      despesaValor: payload.despesaValor,
      tenant: payload.tenant,
    });
  }

  despesaLiquidarTotal(id, payload) {
    return this.axios.put(`/despesa/liquidar-total/${id}`, {
      valor: payload.valor,
      dtMovimentacao: payload.dtMovimentacao,
      historico: payload.historico,
      contaId: payload.contaId,
      despesaValor: payload.despesaValor,
      partialOrigin: payload.partialOrigin,
      tenant: payload.tenant,
    });
  }

  recorrenciaLiquidarParcial(id, payload) {
    return this.axios.put(`/despesa-recorrente/liquidar-parcial/${id}`, {
      valor: payload.valor,
      dtMovimentacao: payload.dtMovimentacao,
      historico: payload.historico,
      contaId: payload.contaId,
      mesLiquidacao: payload.mesLiquidacao,
      despesaValor: payload.despesaValor,
      tenant: payload.tenant,
    });
  }

  recorrenciaLiquidarTotal(id, payload) {
    return this.axios.put(`/despesa-recorrente/liquidar-total/${id}`, {
      valor: payload.valor,
      dtMovimentacao: payload.dtMovimentacao,
      historico: payload.historico,
      contaId: payload.contaId,
      mesLiquidacao: payload.mesLiquidacao,
      despesaValor: payload.despesaValor,
    });
  }

  createDespesa(despesa, files) {
    const data = new FormData();
    data.set("despesa", JSON.stringify(despesa));

    files.forEach((file) => data.append("file", file));

    return this.axios.post(`/despesa/create`, data, {
      "Content-Type": "multipart/form-data",
    });
  }

  createDespesaLiquidada(despesa, date, historico, contaId, valor, files) {
    const liquidacao = {
      dtMovimentacao: date,
      historico: historico,
      contaId: contaId,
      valor,
    };
    const payload = { despesa, liquidacao };

    const formData = new FormData();
    formData.append("payload", JSON.stringify(payload));
    files.forEach((file) => formData.append("file", file));

    return this.axios.post(`/despesa/create-liquidada`, formData, {
      "Content-Type": "multipart/form-data",
    });
  }

  updateDespesa(id, despesa, files) {
    const data = new FormData();
    data.append("despesa", JSON.stringify(despesa));

    files.forEach((file) => data.append("file", file));

    return this.axios.put(`/despesa/update/${id}`, data, {
      "Content-Type": "multipart/form-data",
    });
  }

  getRelatoriosDespesa(despesa) {
    return this.axios.post(`/despesa/relatorios/despesa`, despesa);
  }

  getDespesaDescontoRelatorio(despesa) {
    return this.axios.post(`/despesa/relatorios/desconto`, despesa);
  }

  downloadDespesaAnexo(filename, despesaId) {
    return this.axios.post(
      "/despesa/anexo",
      {
        despesaId: despesaId,
        file: filename,
      },
      { responseType: "blob" }
    );
  }

  estornarPagamentoDespesa(id, tenant) {
    return this.axios.put(`/despesa/estornar/${id}/${tenant}`);
  }

  expenseSuggestion(payload) {
    return this.axios.post("/despesa/sugestao", payload);
  }

  unificarDespesas(payload) {
    return this.axios.post("/despesa/unificar-despesas", payload);
  }

  checkExpenseDuplicity(payload) {
    return this.axios.post("/despesa/check-duplicity", payload);
  }

  getDespesaHistorico(id) {
    return this.axios.get(`/despesa/historico/${id}`);
  }

  // Conhecimento
  getConhecimentosAFaturar(conhecimento) {
    return this.axios.post(`/conhecimento/a-faturar`, conhecimento);
  }

  getConhecimentosTomador(filters) {
    return this.axios.post("/conhecimento/por-tomador", filters);
  }

  getConhecimentosALiquidar(payload) {
    return this.axios.post("/conhecimento/a-liquidar", payload);
  }

  getRelatoriosConhecimentos(conhecimento) {
    return this.axios.post(
      `/conhecimento/relatorios/conhecimento`,
      conhecimento
    );
  }

  liquidarConhecimentos(payload) {
    return this.axios.post("/conhecimento/liquidar-fatura", payload);
  }

  processarArquivoXlsxCte(payload) {
    return this.axios.post("/conhecimento/processar-arquivo", payload);
  }

  // Cidade

  getCidades(amount = 10, offset = 0, filters) {
    return this.axios.get(
      `/cidade/get-cidades-paginated?offset=${offset}&amount=${amount}`,
      { params: { ...filters } }
    );
  }

  getSingleCidade(id) {
    return this.axios.get(`/cidade/show/${id}`);
  }

  createCidade(cidade) {
    return this.axios.post("/cidade/create", cidade);
  }

  updateCidade(id, cidade) {
    return this.axios.put(`/cidade/update/${id}`, cidade);
  }

  getUfs() {
    return this.axios.get("/uf/all");
  }

  getSingleUf(id) {
    return this.axios.get(`/uf/show/${id}`);
  }

  createUf(uf) {
    return this.axios.post("/uf/create", uf);
  }

  updateUf(id, uf) {
    return this.axios.put(`/uf/update/${id}`, uf);
  }

  //Grupos faturamento
  getSingleGruposFaturamento(id) {
    return this.axios.get(`/grupo-faturamento/show/${id}`);
  }

  getGruposFaturamento(tenant) {
    return this.axios.post("/grupo-faturamento/get-all", { tenant });
  }

  createGrupoFaturamento(payload) {
    return this.axios.post("/grupo-faturamento/create", payload);
  }

  updateGrupoFaturamento(id, payload) {
    return this.axios.put(`/grupo-faturamento/update/${id}`, payload);
  }

  // Grupos pessoa
  getSingleGrupoPessoa(id) {
    return this.axios.get(`/grupo-pessoa/show/${id}`);
  }

  getGruposPessoa(tenant) {
    return this.axios.post("/grupo-pessoa/get-all", { tenant });
  }

  createGrupoPessoa(payload) {
    return this.axios.post("/grupo-pessoa/create", payload);
  }

  updateGrupoPessoa(id, payload) {
    return this.axios.put(`/grupo-pessoa/update/${id}`, payload);
  }

  deleteGrupoPessoa(id) {
    return this.axios.delete(`/grupo-pessoa/delete/${id}`);
  }

  // Dashboard
  dashboardTotalByMonth(tenant) {
    return this.axios.post("/dashboard/movimentacao-por-mes", { tenant });
  }

  // Motivo bloqueio
  getMotivosBloqueio(tenant) {
    return this.axios.get(`/motivo-bloqueio/tenant/${tenant}`);
  }

  getSingleMotivoBloqueio(id) {
    return this.axios.get(`/motivo-bloqueio/show/${id}`);
  }

  createMotivoBloqueio(motivo) {
    return this.axios.post("/motivo-bloqueio/create", motivo);
  }

  updateMotivoBloqueio(id, motivo) {
    return this.axios.put(`/motivo-bloqueio/update/${id}`, motivo);
  }

  bloqueioConhecimentoGrid(payload) {
    return this.axios.post(`/conhecimento/conhecimentos-a-bloquear`, payload);
  }

  bloquearConhecimentos(payload) {
    return this.axios.put(`/conhecimento/bloquear`, payload);
  }

  desbloquearConhecimento(payload) {
    return this.axios.put(`/conhecimento/desbloquear`, payload);
  }

  //Agrupamentos Demonstração
  getAgrupamentosDemonstracao(tenant) {
    return this.axios.post("/agrupamento-demonstracao/all", {
      tenant,
    });
  }

  getSingleAgrupamentoDemonstacao(id, tenant) {
    return this.axios.get(`/agrupamento-demonstracao/single/${tenant}/${id}`);
  }

  createAgrupamentoDemonstacao(agrupamento) {
    return this.axios.post(`/agrupamento-demonstracao/create`, agrupamento);
  }

  updateAgrupamentoDemonstacao(id, agrupamento) {
    return this.axios.put(
      `/agrupamento-demonstracao/update/${id}`,
      agrupamento
    );
  }

  //Emquadramento Conta
  getEnquadramentos(tenant) {
    return this.axios.post("/enquadramento-conta/all", {
      tenant,
    });
  }

  getEnquadramentosTotais(payload) {
    return this.axios.post(
      "/enquadramento-conta/enquadramentos-totais",
      payload
    );
  }

  singleEnquadramento(id) {
    return this.axios.get(`/enquadramento-conta/single/${id}`);
  }

  createEnquadramentoConta(enquadramento) {
    return this.axios.post(`/enquadramento-conta/create`, enquadramento);
  }

  updateEnquadramentoConta(id, enquadramento) {
    return this.axios.put(`/enquadramento-conta/update/${id}`, enquadramento);
  }

  getDREInfo(payload) {
    return this.axios.post("/dre/dre-info", payload);
  }

  gerarEstruturaPadraoDRE(tenant) {
    return this.axios.post("/dre/estrutura-padrao", { tenant });
  }

  estornarReceitaCte(payload) {
    return this.axios.put("/receita/estornar-por-cte/", payload);
  }

  criarConvenio(payload) {
    return this.axios.post("/convenio/create", payload);
  }

  // planos
  criarPlano(payload) {
    return this.axios.post("/plano/create", payload);
  }

  getPlano(id) {
    return this.axios.get(`/plano/show/${id}`);
  }

  updatePlano(id, plano) {
    return this.axios.put(`/plano/update/${id}`, plano);
  }

  getPlanos(tenant) {
    return this.axios.get(`/plano/tenant/${tenant}`);
  }

  getClientesPorPlano(payload) {
    return this.axios.post("/plano/clientes-por-plano", payload);
  }

  atualizarValorPlanos(payload) {
    return this.axios.post("/plano/atualizar-valor-planos", payload);
  }

  // conciliacao
  sendOfxFile(payload) {
    return this.axios.post("/conciliacao/processar-arquivo", payload);
  }

  conciliacaoPorPeriodo(payload) {
    return this.axios.post("/conciliacao/por-periodo", payload);
  }

  excluirMovimentacoes(payload) {
    return this.axios.post("/conciliacao/excluir-movimentacoes", payload);
  }

  editarRegistroConciliacao(id, payload) {
    return this.axios.put(`/conciliacao/update/${id}`, payload);
  }

  undoReconciliation(id, payload) {
    return this.axios.put(`/conciliacao/undo/${id}`, payload);
  }

  // NFS-e
  findNfseByField(field, value, tenant) {
    return this.axios.post("/nota-fiscal-servico/find-by-field", {
      field,
      value,
      tenant,
    });
  }

  // Arquivos
  getRemessas(filters) {
    return this.axios.post("/remessa/get-remessas/", filters);
  }

  downloadRemessa(payload) {
    return this.axios.post("/remessa/download", payload);
  }

  getFaturasRemessa(payload) {
    return this.axios.post("/remessa/faturas", payload);
  }

  // #region EDI
  async getEdiLayouts(amount = 10, offset = 0, filters = {}) {
    try {
      const response = await this.axios.get('/edi/layouts', {
        params: {
          tenant: filters.tenant,
          tipoLayout: filters.tipoLayout,
          ativo: filters.ativo,
          limit: amount,
          offset,
        },
      });
      return { data: response.data.data };
    } catch (error) {
      return { error };
    }
  }

  async getEdiLayoutConfigById(id) {
    try {
      const response = await this.axios.get(`/edi/layouts/${id}`);
      return { data: response.data.data };
    } catch (error) {
      return { error };
    }
  }

  createEdiLayout(payload) {
    return this.axios.post('/edi/layouts', sanitizeEdiLayoutCreatePayload(payload));
  }

  updateEdiLayout(id, payload) {
    return this.axios.put(`/edi/layouts/${id}`, sanitizeEdiLayoutUpdatePayload(payload));
  }

  setEdiLayoutAtivo(id, ativo) {
    return this.axios.patch(`/edi/layouts/${id}/ativo`, { ativo });
  }

  async getEdiCamposSistema(tipoLayout = 'DOCCOB') {
    try {
      const response = await this.axios.get('/edi/campos-sistema', {
        params: { tipoLayout },
      });
      return { data: response.data.data };
    } catch (error) {
      return { error };
    }
  }

  async getEdiCatalogo(tipoLayout = 'DOCCOB', registroId) {
    try {
      const response = await this.axios.get('/edi/catalogo', {
        params: { tipoLayout, registroId },
      });
      return { data: response.data.data };
    } catch (error) {
      return { error };
    }
  }

  async getEdiLayoutPadrao(tipoLayout = 'DOCCOB') {
    const response = await this.getEdiCatalogo(tipoLayout);
    if (response.error) return response;
    const linhas = (response.data || []).map((item) => ({
      campoCatalogoId: item.id,
      registroId: item.registroId,
      descricao: item.nome,
      codigoCampoSistema: item.codigoCampoSistemaSugerido,
      posicaoInicial: item.posicaoInicial,
      posicaoFinal: item.posicaoFinal,
      tamanho: item.tamanho,
      tipoDado: item.formatoTipo,
      obrigatorio: item.mandatorio,
      decimais: item.casasDecimais,
    }));
    return { data: { linhas } };
  }

  async getLayoutEdiVinculos(filters = {}) {
    try {
      const response = await this.axios.get('/edi/vinculos', {
        params: {
          tenant: filters.tenant,
          tipoLayout: filters.tipoLayout,
          ...(filters.pessoaId != null && { pessoaId: filters.pessoaId }),
          ...(filters.layoutId != null && { layoutId: filters.layoutId }),
        },
      });
      return { data: response.data.data };
    } catch (error) {
      return { error };
    }
  }

  getEdiVinculosPorPessoa(filters = {}) {
    return this.getLayoutEdiVinculos(filters);
  }

  updateEdiFeatureFlag(pessoaId, { tipoLayout, ativa }) {
    return this.axios.patch(`/edi/feature-flag/${pessoaId}`, { tipoLayout, ativa });
  }

  vincularEdiLayout(payload) {
    return this.axios.post('/edi/vinculos', payload);
  }

  atualizarEdiVinculo(vinculoId, data) {
    return this.axios.put(`/edi/vinculos/${vinculoId}`, data);
  }

  desvincularEdiLayout(vinculoId) {
    return this.axios.delete(`/edi/vinculos/${vinculoId}`);
  }

  gerarArquivoDoccob(payload) {
    return this.axios.post('/edi/geracoes', payload);
  }

  listEdiGeracoes(filters = {}) {
    return this.axios.get('/edi/geracoes', { params: filters });
  }

  getEdiGeracaoById(id) {
    return this.axios.get(`/edi/geracoes/${id}`);
  }

  downloadEdiGeracao(id) {
    return this.axios.get(`/edi/geracoes/${id}/download`, {
      responseType: 'blob',
    });
  }

  // #endregion EDI

  /**
   * @description Receives an object and removes blank strings and empty arrays
   * @param {Object} fields
   */
  static removeBlankFields(fields = {}) {
    const finalObject = {};
    Object.keys(fields).forEach((field) => {
      if (
        fields[field] &&
        (fields[field].length || typeof fields[field] === "number")
      ) {
        finalObject[`${field}`] = fields[field];
      }
    });
    return finalObject;
  }

  // Faturamento por Plano
  getPlanosAfaturarPeriodo(dataInicio, dataFim, tenant) {
    return this.axios.post("/faturamento-por-plano/listar", {
      dataInicio,
      dataFim,
      tenant,
    });
  }

  faturarPorPlano(payload) {
    return this.axios.post("/faturamento-por-plano/faturar", payload);
  }

  iniciarFaturamentoCicloAutomatizado(payload) {
    return this.axios.post("/faturamento-ciclo/iniciar", payload);
  }

  // Monitoramento do ciclo de faturamento
  getFaturamentoCiclos(filters = {}) {
    return this.axios.get("/faturamento-ciclo/historico", {
      params: Api.removeBlankFields(filters),
    });
  }

  getFaturamentoCicloStatus(correlationId, tenant) {
    return this.axios.get(`/faturamento-ciclo/status/${correlationId}`, {
      params: { tenant },
    });
  }

  // filtros: { faturaId, action } — recortes consultados no banco (o teto de eventos
  // do backend é aplicado após o filtro; sem recorte, ciclos grandes ficam truncados)
  getFaturamentoCicloEventos(correlationId, tenant, filtros = {}) {
    return this.axios.get(`/faturamento-ciclo/${correlationId}/eventos`, {
      params: Api.removeBlankFields({ tenant, ...filtros }),
    });
  }

  reenviarEmailCiclo({ correlationId, tenant, destinatarios }) {
    return this.axios.post(`/faturamento-ciclo/${correlationId}/notificar`, {
      tenant,
      destinatarios,
    });
  }

  reprocessarFaturamentoCiclo({ correlationId, tenant }) {
    return this.axios.post(`/faturamento-ciclo/reprocessar/${correlationId}`, {
      tenant,
    });
  }

  // Cobrança ao cliente do ciclo (painel de cobranças enviadas)
  // filtros: { situacao } — filtro opcional por situação do envio; vazio lista todos.
  // `tenant` vai em params, como nos demais GET do recurso ciclo.
  getCobrancasCiclo(correlationId, tenant, filtros = {}) {
    return this.axios.get(`/faturamento-ciclo/${correlationId}/cobrancas`, {
      params: Api.removeBlankFields({ tenant, ...filtros }),
    });
  }

  // Dados de preenchimento do reenvio de um cliente (faturas concluídas + destinatários do cadastro).
  getPreenchimentoReenvioCobranca(correlationId, pessoaId, tenant) {
    return this.axios.get(
      `/faturamento-ciclo/${correlationId}/cobrancas/clientes/${pessoaId}`,
      { params: { tenant } },
    );
  }

  // Reenvio manual da cobrança de um cliente. `tenant` no body, como no reenvio do resumo (202 assíncrono).
  reenviarCobrancaCliente({
    correlationId, pessoaId, tenant, destinatarios,
  }) {
    return this.axios.post(
      `/faturamento-ciclo/${correlationId}/cobrancas/clientes/${pessoaId}/reenviar`,
      { tenant, destinatarios },
    );
  }

  visualizarFaturamentoPorPlano(payload) {
    return this.axios.post("/faturamento-por-plano/visualizar-faturamento", payload);
  }

  planosPorPessoa(sacadoId, tenant, dataInicio, dataFim) {
    return this.axios.post("/faturamento-por-plano/listar-por-sacado", {
      sacadoId,
      tenant,
      dataInicio,
      dataFim,
    });
  }

  faturamentoPorPlanoReportXlsx(payload) {
    return this.axios.post("/faturamento-por-plano/visualizacao-xlsx", payload);
  }

  faturamentoPorPlanoReportPdf(payload) {
    return this.axios.post("/faturamento-por-plano/visualizacao-pdf", payload);
  }

  // Protocolo Fatura
  getProtocolosFatura(tenant) {
    return this.axios.post("/protocolo-fatura/get-by-tenant", { tenant });
  }

  getProtocoloFatura(id) {
    return this.axios.get(`/protocolo-fatura/show/${id}`);
  }

  createProtocoloFatura(protocoloFatura) {
    return this.axios.post("/protocolo-fatura/create", protocoloFatura);
  }

  updateProtocoloFatura(id, protocoloFatura) {
    return this.axios.put(`/protocolo-fatura/update/${id}`, protocoloFatura);
  }

  baixarProtocolo(id, protocoloFatura) {
    return this.axios.post(`/protocolo-fatura/baixar/${id}`, protocoloFatura);
  }

  getPdfProcolo(id) {
    return this.axios.get(`/protocolo-fatura/imprimir/${id}`);
  }

  // static
  downloadModeloLiquidacaoCte() {
    return this.axios.get("/static/modelo-liquidar-cte");
  }

  // disponibilidade
  getDisponibilidades(payload) {
    return this.axios.post("/disponibilidade/get-disponibilidades", payload);
  }

  getDisponibilidadeFiliais(tenant) {
    return this.axios.get(`/disponibilidade/get-filiais/${tenant}`);
  }

  getRomaneiosAFaturar(filters) {
    return this.axios.get("/disponibilidade/get-romaneios-faturar", {
      params: {
        ...filters,
      },
    });
  }

  getRomaneiosVeiculo(filters) {
    return this.axios.get("/disponibilidade/get-romaneios-veiculo", {
      params: {
        ...filters,
      },
    });
  }

  faturarDisponibilidades(payload) {
    return this.axios.post("/disponibilidade/faturar-disponibilidade", payload);
  }

  // MensagemEmail

  getMensagensEmail() {
    return this.axios.get(`/mensagem-email/all`);
  }

  getSingleMensagemEmail(id) {
    return this.axios.get(`/mensagem-email/show/${id}`);
  }

  createMensagemEmail(mensagemEmail) {
    return this.axios.post("/mensagem-email/create", mensagemEmail);
  }

  updateMensagemEmail(id, mensagemEmail) {
    return this.axios.put(`/mensagem-email/update/${id}`, mensagemEmail);
  }

  deleteMensagemEmail(id) {
    return this.axios.delete(`/mensagem-email/delete/${id}`);
  }

  // Relatórios

  getRelatorioConhecimentosXlsx(payload) {
    return this.axios.post(`/relatorios/conhecimentos/xlsx`, payload);
  }

  getRelatorioReceitaXlsx(payload) {
    return this.axios.post(`/relatorios/receitas/xlsx`, payload);
  }

  getRelatorioReceitaPlanosXlsx(payload) {
    return this.axios.post(`/relatorios/receitas-planos/xlsx`, payload);
  }

  getRelatorioDespesaXlsx(payload) {
    return this.axios.post(`/relatorios/despesas/xlsx`, payload);
  }

  getRelatorioMovimentacaoXlsx(payload) {
    return this.axios.post(`/relatorios/movimentacoes/xlsx`, payload);
  }

  getRelatorioPagamentoXlsx(payload) {
    return this.axios.post("/relatorios/pagamentos/xlsx", payload);
  }

  getNotaFiscalXlsx(payload) {
    return this.axios.post("/relatorios/nota-fiscal/xlsx", payload);
  }

  getRelatorioPessoaXlsx(payload) {
    return this.axios.post(`/relatorios/pessoas/xlsx`, payload);
  }

  getRelatorioPessoaPdf(payload) {
    return this.axios.post(`/relatorios/pessoas/pdf`, payload);
  }

  getRelatorioDespesaDescontoXlsx(payload) {
    return this.axios.post(`/relatorios/despesas-descontos/xlsx`, payload);
  }

  getRelatorioReceitaDescontoXlsx(payload) {
    return this.axios.post(`/relatorios/receitas-descontos/xlsx`, payload);
  }

  // CARGOZILLA-8277 — Pré Extrato do arquivo de retorno bancário.
  getRelatorioRetornoBancario(payload) {
    return this.axios.post('/relatorios/retorno-bancario', payload);
  }

  getRelatorioRetornoBancarioXlsx(payload) {
    return this.axios.post('/relatorios/retorno-bancario/xlsx', payload);
  }

  // indicadores saas
  getCac(filters) {
    return this.axios.get("/indicadores-saas/cac", {
      params: {
        ...filters,
      },
    });
  }

  getMrr(filters) {
    return this.axios.get("/indicadores-saas/mrr", {
      params: {
        ...filters,
      },
    });
  }

  getArr(filters) {
    return this.axios.get("/indicadores-saas/arr", {
      params: {
        ...filters,
      },
    });
  }

  getTicketMedio(filters) {
    return this.axios.get("/indicadores-saas/ticket-medio", {
      params: {
        ...filters,
      },
    });
  }
  getLtv(filters) {
    return this.axios.get("/indicadores-saas/ltv", {
      params: {
        ...filters,
      },
    });
  }

  getChurnRate(filters) {
    return this.axios.get("/indicadores-saas/churn-rate", {
      params: {
        ...filters,
      },
    });
  }

  getGrowthRate(filters) {
    return this.axios.get("/indicadores-saas/growth-rate", {
      params: {
        ...filters,
      },
    });
  }

  getMrrRate(filters) {
    return this.axios.get("/indicadores-saas/mrr-rate", {
      params: {
        ...filters,
      },
    });
  }

  getCashBurnRunway(filters) {
    return this.axios.get("/indicadores-saas/cash-burn-runway", {
      params: {
        ...filters,
      },
    });
  }

  // Nota Serviço

  createNotaServico(payload) {
    return this.axios.post("/nota-servico/create", payload);
  }

  editNotaServico(id, payload) {
    return this.axios.put(`/nota-servico/update/${id}`, payload);
  }

  deleteNotaServico(id) {
    return this.axios.delete(`/nota-servico/delete/${id}`);
  }

  getAllNotasServico(filters) {
    return this.axios.get(`/nota-servico`, {
      params: {
        ...filters,
      },
    });
  }

  createNotaServicoHistorico(id, payload) {
    return this.axios.post(`/nota-servico-historico/${id}/create`, payload);
  }

  enviarNfsPrefeitura(municipio, ambiente, provider, xml, payload) {
    return this.axios.post(`/nota-servico/prefeitura-enviar`, payload, {
      headers: {
        municipio,
        ambiente,
        provider,
        xml,
      },
    });
  }

  cancelarNfsPrefeitura(municipio, ambiente, provider, xml, payload) {
    return this.axios.post(`/nota-servico/prefeitura-cancelar`, payload, {
      headers: {
        municipio,
        ambiente,
        provider,
        xml,
      },
    });
  }

  consultarNfsPrefeitura(municipio, ambiente, provider, xml, payload) {
    return this.axios.post(`/nota-servico/prefeitura-consultar`, payload, {
      headers: {
        municipio,
        ambiente,
        provider,
        xml,
      },
    });
  }

  nfsSequencial(id) {
    return this.axios.get(`/pessoa/nfs-sequencial-rps/${id}`);
  }

// #region E-mail Template
createEmailTemplate(payload) {
  return this.axios.post("/email-template/create", payload);
}

editEmailTemplate(id, payload) {
  return this.axios.put(`/email-template/update/${id}`, payload);
}

getEmailTemplateById(id) {
  return this.axios.get(`/email-template/show/${id}`);
}

deleteEmailTemplate(id) {
  return this.axios.delete(`/email-template/delete/${id}`);
}

getAllEmailTemplate(filters) {
  return this.axios.get(`/email-template/all`, {
    params: {
      ...filters,
    },
  });
}

enviarEmailTemplate(payload) {
  return this.axios.post(`/email-template/send-email`, payload);
}
// #endregion E-mail Template

// #region Configuração E-mail (remetente/servidor do tenant — CARGOZILLA-8215)
getConfiguracaoEmail(tenant) {
  return this.axios.get('/configuracao-email', { params: { tenant } });
}

salvarConfiguracaoEmail(payload) {
  return this.axios.put('/configuracao-email', payload);
}

testarConfiguracaoEmail(payload) {
  return this.axios.post('/configuracao-email/testar', payload);
}
// #endregion Configuração E-mail

// #region Regua Cobrança
createReguaCobranca(payload) {
  return this.axios.post("/regua-cobranca/create", payload);
}

editReguaCobranca(id, payload) {
  return this.axios.put(`/regua-cobranca/update/${id}`, payload);
}

getReguaCobrancaById(id) {
  return this.axios.get(`/regua-cobranca/show/${id}`);
}

deleteReguaCobranca(id) {
  return this.axios.delete(`/regua-cobranca/delete/${id}`);
}

getAllReguaCobranca(filters) {
  return this.axios.get(`/regua-cobranca/all`, {
    params: {
      ...filters,
    },
  });
}
// #endregion Regua Cobrança

// #region Bloqueio de Cliente
createBloqueioCliente(payload) {
  return this.axios.post("/bloqueio-cliente/create", payload);
}

editBloqueioCliente(id, payload) {
  return this.axios.put(`/bloqueio-cliente/update/${id}`, payload);
}

getBloqueioClienteById(id) {
  return this.axios.get(`/bloqueio-cliente/show/${id}`);
}

deleteBloqueioCliente(id) {
  return this.axios.delete(`/bloqueio-cliente/delete/${id}`);
}

getAllBloqueioCliente(filters) {
  return this.axios.get(`/bloqueio-cliente/all`, {
    params: {
      ...filters,
    },
  });
}
// #endregion  Bloqueio de Cliente

// #region Integrações Externas
createIntegracaoExterna(payload) {
  return this.axios.post("/integracoes-externas/create", payload);
}

editIntegracaoExterna(id, payload) {
  return this.axios.put(`/integracoes-externas/update/${id}`, payload);
}

getIntegracaoExternaById(id) {
  return this.axios.get(`/integracoes-externas/show/${id}`);
}

deleteIntegracaoExterna(id) {
  return this.axios.delete(`/integracoes-externas/delete/${id}`);
}

getAllIntegracaoExternas(filters) {
  return this.axios.get(`/integracoes-externas/all`, {
    params: {
      ...filters,
    },
  });
}

async getAllPessoaIntegracoesExternas(tenant) {
  return this.axios.get(`/pessoa-integracoes-externas/matriz/${tenant}`);
}
// #endregion Integrações Externas

  // #region Esteira de Conhecimentos
  // Registro de estado dos conhecimentos integrados (CARGOZILLA-8204/8205). A tela de
  // conferência é a única interface destes endpoints — não há painel de exceções separado.

  // Listagem paginada da tela. Devolve { rows, count, contagemPorEstado }: a contagem por
  // estado vem sob os MESMOS filtros da página, sem segunda chamada.
  // `estado` e `origem` aceitam string ou array (axios serializa `estado[]=A&estado[]=B`,
  // que o qs do Express remonta como array).
  getEsteiraConhecimentos(filtros) {
    return this.axios.get("/esteira-conhecimento", { params: { ...filtros } });
  }

  // Consulta pontual com a trilha de transições. Exige `chave` OU `numero` OU a janela
  // (`dataInicio` + `dataFim` sempre em par) — janela aberta de um lado o backend recusa.
  getEsteiraConhecimentoConsulta(filtros) {
    return this.axios.get("/esteira-conhecimento/consulta", {
      params: { ...filtros },
    });
  }

  // Simulação do reprocessamento: não escreve nada e devolve o `token` que a execução em
  // lote exige. Válido por ~15min e amarrado ao conjunto exato de ids simulado.
  simularReprocessamentoEsteira(payload) {
    return this.axios.post(
      "/esteira-conhecimento/reprocessamento/simulacao",
      payload
    );
  }

  // Execução auditada. Mande os MESMOS ids da simulação junto do `token` — o token é um
  // HMAC do conjunto pedido, e recortar a lista para só os elegíveis o invalida.
  reprocessarEsteira(payload) {
    return this.axios.post("/esteira-conhecimento/reprocessamento", payload);
  }

  // Exportação da listagem filtrada (abas Registros + Trilha). Janela obrigatória.
  getEsteiraConhecimentosXlsx(payload) {
    return this.axios.post(
      "/esteira-conhecimento/relatorios/trilha/xlsx",
      payload
    );
  }
  // #endregion Esteira de Conhecimentos
}